import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Modal, Button, Input, message } from "antd";
import type { InputRef } from "antd";
import styled, { keyframes } from "styled-components";
import axios, { OrchestratorAPI } from "../../common/api/axios";
import { formatSlotDisplay } from "../../common/utils/slot";
import {
  flattenStaticPickWallSlots,
  StaticPickWallSlot,
} from "../../common/utils/staticPickWall";

// ── Types ──

interface SlotPosition {
  x: number;
  y: number;
  z: number;
}

interface Gate {
  slot: SlotPosition;
  processingQueue: any[];
  group: string;
  mappedPtlDeviceId: string;
  isDisabled: boolean;
}

interface PickStation {
  pickStationId: string;
  mappedPutWallId: string;
  gates: Gate[];
  purpose: number;
  originalPurpose: number;
}

interface Orchestration {
  id: string;
  name: string;
  status: string;
  operationMode: string;
  currentRun: boolean;
  creationDate: string;
  lastUpdatedDate: string;
}

interface CycleCountTask {
  id: string;
  taskType: string;
  cycleCountTask: {
    binId: string;
    skuId: string;
    batchId?: string;
    caseId?: string;
    partition?: string;
    expectedQuantity: number;
    gateLocation: SlotPosition;
    operatorTaskId: string;
  };
  isComplete: boolean;
  creationDate: string;
}

interface SubmitErrorState {
  kind: "validation" | "server";
  message?: string;
}

interface StaticCaseSku {
  skuId: string;
  batchId?: string;
  expectedQuantity: number;
}

interface StaticCaseGroup {
  caseId: string | null;
  skus: StaticCaseSku[];
  caseTotal: number;
}

const ACTIVE_STATUSES = ["StepExecuted"];
const REQUEST_HEADERS = { Accept: "application/json", tenantId: 1 };
const SCANNED_DATA_ERROR = {
  title: "ERROR DETECTED",
  headline: "Discrepancy in mapped Bin or SKU ID.",
  description: "The scanned data does not exist or does not match the active task.",
};

export interface CycleCountProps {
  configName?: string;
}

function slotKey(pos: SlotPosition): string {
  return `${pos.x},${pos.y},${pos.z}`;
}

export default function CycleCount({ configName }: CycleCountProps) {
  const [stations, setStations] = useState<PickStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [cycleCountActive, setCycleCountActive] = useState(false);
  const [orchestratorLoading, setOrchestratorLoading] = useState(true);
  const [exitingCycleCount, setExitingCycleCount] = useState(false);

  const [tasks, setTasks] = useState<CycleCountTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<CycleCountTask | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal state
  const [scannedBinId, setScannedBinId] = useState("");
  const [scannedSkuId, setScannedSkuId] = useState("");
  const [countedQuantity, setCountedQuantity] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitErrorState | null>(null);

  const [staticSlots, setStaticSlots] = useState<StaticPickWallSlot[]>([]);
  const [selectedStaticSlot, setSelectedStaticSlot] =
    useState<StaticPickWallSlot | null>(null);
  const [staticCountedTotal, setStaticCountedTotal] = useState("");

  const binInputRef = useRef<InputRef>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const orchestratorPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [messageApi, contextHolder] = message.useMessage();

  // ── Data Fetching ──

  const fetchStations = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/pickStations", {
        headers: REQUEST_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setStations(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch pick stations:", error);
    }
  }, []);

  const checkCycleCountOrchestration = useCallback(async (): Promise<boolean> => {
    try {
      const res = await OrchestratorAPI.get("orchestrations", {
        headers: REQUEST_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);

        const activeCycleCount = res.data.find(
          (orch: Orchestration) =>
            orch.operationMode === "CycleCounting" &&
            ACTIVE_STATUSES.includes(orch.status) &&
            new Date(orch.creationDate) >= oneDayAgo
        );
        const hasActiveCycleCount = !!activeCycleCount;
        setCycleCountActive(hasActiveCycleCount);
        if (!hasActiveCycleCount) {
          setExitingCycleCount(false);
        }
        return hasActiveCycleCount;
      }
    } catch (error) {
      console.error("Failed to check cycle count orchestration:", error);
      setCycleCountActive(false);
      return false;
    } finally {
      setOrchestratorLoading(false);
    }
    return false;
  }, []);

  const fetchCycleCountTasks = useCallback(async () => {
    try {
      const res = await axios.get("PickStation/cycleCountTasks", {
        headers: REQUEST_HEADERS,
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setTasks(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch cycle count tasks:", error);
    }
  }, []);

  const fetchStaticPickWalls = useCallback(async () => {
    try {
      const res = await axios.get("StaticPickWall", { headers: REQUEST_HEADERS });
      if (res.status === 200) {
        setStaticSlots(flattenStaticPickWallSlots(res.data));
      }
    } catch (error) {
      console.error("Failed to fetch static pick walls:", error);
      setStaticSlots([]);
    }
  }, []);

  const handleExitCycleCount = async () => {
    if (exitingCycleCount || !cycleCountActive) return;

    setExitingCycleCount(true);
    try {
      await OrchestratorAPI.post(
        "orchestrations/exitCycleCounting",
        {},
        { headers: REQUEST_HEADERS }
      );
      const stillActive = await checkCycleCountOrchestration();
      messageApi.success(
        stillActive
          ? "Exit requested. Cycle count will stop after current operations complete."
          : "Cycle count operation exited successfully."
      );
    } catch (error) {
      console.error("Failed to exit cycle count:", error);
      setExitingCycleCount(false);
      messageApi.error("Failed to exit cycle count. Please try again.");
    }
  };


  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchStations(),
      checkCycleCountOrchestration(),
      fetchStaticPickWalls(),
    ]).finally(() => setLoading(false));
  }, [fetchStations, checkCycleCountOrchestration, fetchStaticPickWalls]);


  useEffect(() => {
    orchestratorPollRef.current = setInterval(() => {
      checkCycleCountOrchestration();
    }, 5000);

    return () => {
      if (orchestratorPollRef.current) clearInterval(orchestratorPollRef.current);
    };
  }, [checkCycleCountOrchestration]);


  useEffect(() => {
    if (cycleCountActive) {
      fetchCycleCountTasks();
      pollRef.current = setInterval(fetchCycleCountTasks, 3000);
    } else {
      setTasks([]);
      if (pollRef.current) clearInterval(pollRef.current);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [cycleCountActive, fetchCycleCountTasks]);


  useEffect(() => {
    if (cycleCountActive) {
      fetchStations();
    }
  }, [cycleCountActive, fetchStations]);


  const gates: Gate[] = stations.flatMap((s) => s.gates);

  const buildRows = () => {
    const firstRow: string[] = [];
    const secondRow: string[] = [];

    stations.forEach((station) => {
      if (!Array.isArray(station?.gates)) return;
      station.gates.forEach((gate) => {
        const { x, y, z } = gate.slot;
        const coord = `${x},${y},${z}`;
        if (z === 2) firstRow.push(coord);
        else if (z === 1) secondRow.push(coord);
      });
    });

    const uniqueAndSortDesc = (entries: string[]) =>
      Array.from(new Set(entries)).sort((a, b) => {
        const ax = Number(a.split(",")[0]);
        const bx = Number(b.split(",")[0]);
        return bx - ax;
      });

    return {
      firstRow: uniqueAndSortDesc(firstRow),
      secondRow: uniqueAndSortDesc(secondRow),
    };
  };

  const { firstRow, secondRow } = buildRows();


  const getTaskForSlot = (slotCoord: string): CycleCountTask | null => {
    return (
      tasks.find((t) => {
        const gate = t.cycleCountTask?.gateLocation;
        if (!gate) return false;
        return `${gate.x},${gate.y},${gate.z}` === slotCoord;
      }) ?? null
    );
  };


  const handleSlotClick = (slotCoord: string) => {
    if (!cycleCountActive) return;
    const task = getTaskForSlot(slotCoord);
    if (!task || task.isComplete) return;

    setSelectedTask(task);
    setScannedBinId("");
    setScannedSkuId("");
    setCountedQuantity(task.cycleCountTask.expectedQuantity);
    setSubmitError(null);
    setIsModalOpen(true);

    setTimeout(() => binInputRef.current?.focus(), 300);
  };

  const handleModalClose = () => {
    if (!submitting) {
      setIsModalOpen(false);
      setSelectedTask(null);
      setSelectedStaticSlot(null);
      setSubmitError(null);
    }
  };


  const getStaticSlotTasks = useCallback(
    (slot: StaticPickWallSlot): CycleCountTask[] =>
      tasks.filter((t) => {
        const gate = t.cycleCountTask?.gateLocation;
        return gate ? slotKey(gate) === slot.position : false;
      }),
    [tasks]
  );

  const handleStaticSlotClick = (slot: StaticPickWallSlot) => {
    if (!cycleCountActive || slot.isDisabled) return;

    const pending = getStaticSlotTasks(slot).filter((t) => !t.isComplete);
    if (pending.length === 0) return;

    setSelectedStaticSlot(slot);
    setSelectedTask(null);
    setStaticCountedTotal("");
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const selectedStaticTasks = useMemo(
    () =>
      selectedStaticSlot
        ? getStaticSlotTasks(selectedStaticSlot).filter((t) => !t.isComplete)
        : [],
    [selectedStaticSlot, getStaticSlotTasks]
  );

  const staticCaseGroups: StaticCaseGroup[] = useMemo(() => {
    const groups = new Map<string, StaticCaseGroup>();

    selectedStaticTasks.forEach((task) => {
      const cc = task.cycleCountTask;
      const key = cc.caseId?.trim() ? cc.caseId.trim() : "__no_case__";
      const group =
        groups.get(key) ??
        ({
          caseId: key === "__no_case__" ? null : key,
          skus: [],
          caseTotal: 0,
        } as StaticCaseGroup);

      group.skus.push({
        skuId: cc.skuId,
        batchId: cc.batchId,
        expectedQuantity: cc.expectedQuantity ?? 0,
      });
      group.caseTotal += cc.expectedQuantity ?? 0;
      groups.set(key, group);
    });

    return Array.from(groups.values());
  }, [selectedStaticTasks]);

  const staticTotalQuantity = useMemo(
    () =>
      selectedStaticTasks.reduce(
        (sum, task) => sum + (task.cycleCountTask.expectedQuantity ?? 0),
        0
      ),
    [selectedStaticTasks]
  );

  const staticBatchText = useMemo(() => {
    const batches = Array.from(
      new Set(
        selectedStaticTasks
          .map((task) => task.cycleCountTask.batchId?.trim())
          .filter((batch): batch is string => Boolean(batch))
      )
    );
    return batches.length > 0 ? batches.join(", ") : "N/A";
  }, [selectedStaticTasks]);


  const handleStaticVerifyAndSubmit = async () => {
    if (!selectedStaticSlot || selectedStaticTasks.length === 0) return;

    if (staticCountedTotal.trim() === "" || !/^\d+$/.test(staticCountedTotal)) {
      setSubmitError({ kind: "validation", message: "Enter a valid total quantity." });
      return;
    }

    const counted = Number(staticCountedTotal);
    if (counted !== staticTotalQuantity) {
      setSubmitError({
        kind: "validation",
        message: `Counted total (${counted}) does not match the expected total (${staticTotalQuantity}). Recount the bin to verify.`,
      });
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      for (const task of selectedStaticTasks) {
        await axios.put(`PickStation/completeCycleCount/${task.id}/0`, null, {
          headers: REQUEST_HEADERS,
        });
      }

      messageApi.success("Cycle count verified successfully");
      setIsModalOpen(false);
      setSelectedStaticSlot(null);
      fetchCycleCountTasks();
    } catch (error: any) {
      console.error("Failed to complete static cycle count:", error);
      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        "Failed to complete cycle count. Please try again.";
      setSubmitError({ kind: "server", message: serverMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStaticSlot = (slot: StaticPickWallSlot) => {
    const slotTasks = getStaticSlotTasks(slot);
    const pending = slotTasks.filter((t) => !t.isComplete);
    const footer = (
      <StaticSlotFooter>Bin - {formatSlotDisplay(slot.position)}</StaticSlotFooter>
    );

    if (pending.length > 0) {
      const binId = pending[0].cycleCountTask.binId;
      const caseCount = new Set(
        pending
          .map((t) => t.cycleCountTask.caseId?.trim())
          .filter((caseId) => Boolean(caseId))
      ).size;

      return (
        <StaticSlotCard
          key={slot.id}
          $state="active"
          onClick={() => handleStaticSlotClick(slot)}
        >
          <StaticSlotBody>
            <span>{binId}</span>
            <StaticSlotSub>
              {caseCount > 0
                ? `${caseCount} case${caseCount === 1 ? "" : "s"}`
                : `${pending.length} item${pending.length === 1 ? "" : "s"}`}
            </StaticSlotSub>
          </StaticSlotBody>
          {footer}
        </StaticSlotCard>
      );
    }

    if (slotTasks.length > 0) {
      return (
        <StaticSlotCard key={slot.id} $state="complete">
          <StaticSlotCheck>&#10003;</StaticSlotCheck>
          {footer}
        </StaticSlotCard>
      );
    }

    return (
      <StaticSlotCard key={slot.id} $state="disabled">
        <StaticSlotBody />
        {footer}
      </StaticSlotCard>
    );
  };


  const validateScannedIdentifiers = useCallback(
    async (
      binId: string,
      skuId: string,
      expectedBinId: string,
      expectedSkuId: string
    ) => {
      const trimmedBinId = binId.trim();
      const trimmedSkuId = skuId.trim();

      if (!trimmedBinId || !trimmedSkuId) {
        return false;
      }

      if (trimmedBinId !== expectedBinId.trim() || trimmedSkuId !== expectedSkuId.trim()) {
        return false;
      }

      const [binResponse, skuResponse] = await Promise.all([
        axios.get(`BinMaster/getById/${encodeURIComponent(trimmedBinId)}`, {
          headers: REQUEST_HEADERS,
          validateStatus: () => true,
        }),
        axios.get(`MasterData/sku/${encodeURIComponent(trimmedSkuId)}`, {
          headers: REQUEST_HEADERS,
          validateStatus: () => true,
        }),
      ]);

      return binResponse.status === 200 && skuResponse.status === 200;
    },
    []
  );


  const handleVerifyAndSubmit = async () => {
    if (!selectedTask) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      let hasValidScannedIdentifiers = false;

      try {
        hasValidScannedIdentifiers = await validateScannedIdentifiers(
          scannedBinId,
          scannedSkuId,
          selectedTask.cycleCountTask.binId,
          selectedTask.cycleCountTask.skuId
        );
      } catch (error) {
        console.error("Failed to validate scanned bin or SKU:", error);
        setSubmitError({
          kind: "server",
          message: "Failed to validate scanned bin and SKU. Please try again.",
        });
        return;
      }

      if (!hasValidScannedIdentifiers) {
        setSubmitError({ kind: "validation" });
        return;
      }

      const taskId = selectedTask.id;
      const quantityDelta =
        countedQuantity - selectedTask.cycleCountTask.expectedQuantity;
      await axios.put(
        `PickStation/completeCycleCount/${taskId}/${quantityDelta}`,
        null,
        { headers: REQUEST_HEADERS }
      );

      messageApi.success("Cycle count verified successfully");
      setIsModalOpen(false);
      setSelectedTask(null);
      fetchCycleCountTasks();
    } catch (error: any) {
      console.error("Failed to complete cycle count:", error);
      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        "Failed to complete cycle count. Please try again.";
      setSubmitError({ kind: "server", message: serverMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const hasQuantityDiscrepancy =
    selectedTask != null &&
    countedQuantity !== selectedTask.cycleCountTask.expectedQuantity;


  const renderSlot = (slotCoord: string) => {
    const task = getTaskForSlot(slotCoord);
    const hasTask = !!task && !task.isComplete;
    const isComplete = !!task?.isComplete;

    if (hasTask) {
      return (
        <div
          key={slotCoord}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "280px",
            border: "2px solid #4D9A9A",
            borderRadius: "8px",
            backgroundColor: "#e8e8e8",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onClick={() => handleSlotClick(slotCoord)}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
            }}
          >
            <div
              style={{
                backgroundColor: "transparent",
                border: "2px solid transparent",
                borderRadius: "4px",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <span style={{ color: "#1f5454", fontSize: "14px", fontWeight: 600 }}>
                {task!.cycleCountTask.binId}
              </span>
              <span style={{ color: "#666", fontSize: "12px" }}>
                {task!.cycleCountTask.skuId}
              </span>
            </div>
          </div>
          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#d9d9d9",
              borderTop: "1px solid #c6c6c6",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333",
              borderBottomLeftRadius: "6px",
              borderBottomRightRadius: "6px",
            }}
          >
            Bin - {formatSlotDisplay(slotCoord)}
          </div>
        </div>
      );
    }

    if (isComplete) {
      return (
        <div
          key={slotCoord}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "280px",
            border: "2px solid #52c41a",
            borderRadius: "8px",
            backgroundColor: "#e8e8e8",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "#52c41a",
                borderRadius: "4px",
                width: "calc(100% - 8px)",
                height: "calc(100% - 8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "white", fontSize: "20px" }}>&#10003;</span>
            </div>
          </div>
          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#d9d9d9",
              borderTop: "1px solid #c6c6c6",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333",
              borderBottomLeftRadius: "6px",
              borderBottomRightRadius: "6px",
            }}
          >
            Bin - {formatSlotDisplay(slotCoord)}
          </div>
        </div>
      );
    }

    // Empty / waiting
    return (
      <div
        key={slotCoord}
        style={{
          width: "100%",
          height: "280px",
          backgroundColor: "#f5f5f5",
          border: "2px dashed #d9d9d9",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flex: 1 }} />
        <div
          style={{
            padding: "6px 8px",
            backgroundColor: "#e9e9e9",
            borderTop: "1px solid #d3d3d3",
            textAlign: "center",
            fontSize: "12px",
            fontWeight: "bold",
            color: "#666",
            borderBottomLeftRadius: "6px",
            borderBottomRightRadius: "6px",
          }}
        >
          Bin - {formatSlotDisplay(slotCoord)}
        </div>
      </div>
    );
  };


  if (loading || orchestratorLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LargeSpinner />
          <span>Loading cycle count...</span>
        </LoadingContainer>
        {contextHolder}
      </PageContainer>
    );
  }


  if (!cycleCountActive) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Cycle Count</PageTitle>
        </PageHeader>
        <EmptyStateContainer>
          <span>No active cycle count operation.</span>
          <span style={{ fontSize: 13, color: "#999" }}>
            A cycle count orchestration must be started to enable this screen.
          </span>
        </EmptyStateContainer>
        {contextHolder}
      </PageContainer>
    );
  }


  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Cycle Count — Select Slot</PageTitle>
        <HeaderRight>
          <TaskCount>
            Tasks: <strong>{tasks.filter((t) => !t.isComplete).length}</strong>
          </TaskCount>
        </HeaderRight>
      </PageHeader>

      <GridContainer>
        <SlotsContainer>
          {firstRow.length > 0 && (
            <SlotsRow columns={firstRow.length}>
              {firstRow.map((coord) => renderSlot(coord))}
            </SlotsRow>
          )}
          {secondRow.length > 0 && (
            <SlotsRow columns={secondRow.length}>
              {secondRow.map((coord) => renderSlot(coord))}
            </SlotsRow>
          )}
        </SlotsContainer>

        {staticSlots.length > 0 && (
          <StaticSection>
            <StaticSectionTitle>Static Pickwall</StaticSectionTitle>
            <StaticSlotsRow>
              {staticSlots.map((slot) => renderStaticSlot(slot))}
            </StaticSlotsRow>
          </StaticSection>
        )}

        <ExitButtonRow>
          <ExitCycleCountButton
            disabled={exitingCycleCount}
            onClick={handleExitCycleCount}
          >
            {exitingCycleCount ? "Exiting Cycle Count" : "Exit Cycle Count"}
          </ExitCycleCountButton>
        </ExitButtonRow>
      </GridContainer>

      <Modal
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={960}
        centered
        closable
        destroyOnClose
        styles={{
          content: {
            background: "#fff",
            borderRadius: "16px",
            padding: "28px",
          },
          body: {
            padding: "0",
          },
        }}
      >
        {selectedStaticSlot ? (
          <ModalContainer>
            <Column>
              <ColumnTitle>VERIFICATION</ColumnTitle>

              <FormCard>
                <FormLabel>TOTAL QUANTITY</FormLabel>
                <Input
                  placeholder="Enter counted total"
                  value={staticCountedTotal}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d+$/.test(value)) {
                      setStaticCountedTotal(value);
                      setSubmitError(null);
                    }
                  }}
                  size="large"
                  style={{ marginTop: 8 }}
                />
              </FormCard>

              <FormCard>
                <FormLabel>BATCH ID</FormLabel>
                <FormValue>{staticBatchText}</FormValue>
              </FormCard>

              <FormCard>
                <FormLabel>BIN LOCATION</FormLabel>
                <FormValue>
                  {selectedStaticSlot.x}-{selectedStaticSlot.y}-
                  {selectedStaticSlot.z}
                </FormValue>
              </FormCard>
            </Column>

            <Column>
              <ColumnTitle>QUANTITY</ColumnTitle>

              <CaseListBox>
                {staticCaseGroups.map((group) => (
                  <CaseRow key={group.caseId ?? "__no_case__"}>
                    <CaseRowLabel>
                      <CaseRowCase>
                        {group.caseId ? `Case ${group.caseId}` : "No Case ID"}
                      </CaseRowCase>
                      <CaseRowSku>
                        {group.skus.map((sku) => sku.skuId).join(", ")}
                      </CaseRowSku>
                    </CaseRowLabel>
                    <CaseRowQty>{group.caseTotal}</CaseRowQty>
                  </CaseRow>
                ))}
              </CaseListBox>

              <TotalRow>
                <TotalRowLabel>Total Qty</TotalRowLabel>
                <TotalRowValue>{staticTotalQuantity}</TotalRowValue>
              </TotalRow>
            </Column>

            <Column>
              <ColumnTitle>SUBMISSION</ColumnTitle>

              <div
                style={{
                  color: "#555",
                  fontSize: 14,
                  lineHeight: 1.6,
                  marginBottom: "auto",
                  padding: "0 8px",
                }}
              >
                Count the static bin and enter the <strong>total quantity</strong>{" "}
                to verify and submit the cycle count.
              </div>

              {staticCountedTotal.trim() !== "" &&
                Number(staticCountedTotal) !== staticTotalQuantity && (
                  <QuantityWarning>
                    <QuantityWarningIcon>!</QuantityWarningIcon>
                    <span>Count Discrepancy Detected</span>
                  </QuantityWarning>
                )}

              {(submitError?.kind === "validation" ||
                submitError?.kind === "server") &&
                submitError.message && <ErrorText>{submitError.message}</ErrorText>}

              <VerifyButton
                disabled={submitting}
                loading={submitting}
                onClick={handleStaticVerifyAndSubmit}
              >
                Verify & Submit
              </VerifyButton>
            </Column>
          </ModalContainer>
        ) : selectedTask ? (
          <ModalContainer>
            <Column>
              <ColumnTitle>VERIFICATION</ColumnTitle>

              <FormCard>
                <FormLabel>BIN ID</FormLabel>
                <FormValue>{selectedTask.cycleCountTask.binId}</FormValue>
                <Input
                  ref={binInputRef}
                  placeholder="Scan Bin ID"
                  value={scannedBinId}
                  onChange={(e) => {
                    setScannedBinId(e.target.value);
                    setSubmitError(null);
                  }}
                  size="large"
                  style={{ marginTop: 8 }}
                />
              </FormCard>

              <FormCard>
                <FormLabel>SKU ID</FormLabel>
                <FormValue>{selectedTask.cycleCountTask.skuId}</FormValue>
                <Input
                  placeholder="Scan SKU ID"
                  value={scannedSkuId}
                  onChange={(e) => {
                    setScannedSkuId(e.target.value);
                    setSubmitError(null);
                  }}
                  size="large"
                  style={{ marginTop: 8 }}
                />
              </FormCard>

              <FormCard>
                <FormLabel>BATCH ID</FormLabel>
                <FormValue>{selectedTask.cycleCountTask.batchId || "N/A"}</FormValue>
              </FormCard>

              <FormCard>
                <FormLabel>BIN LOCATION</FormLabel>
                <FormValue>
                  {selectedTask.cycleCountTask.gateLocation.x}-
                  {selectedTask.cycleCountTask.gateLocation.y}-
                  {selectedTask.cycleCountTask.gateLocation.z}
                </FormValue>
              </FormCard>
            </Column>

            <Column>
              <ColumnTitle>QUANTITY</ColumnTitle>

              <FormCard style={{ textAlign: "center", flex: "0 0 auto" }}>
                <FormLabel>EXPECTED QTY</FormLabel>
                <div style={{ fontSize: 36, fontWeight: 800, padding: "8px 0" }}>
                  {selectedTask.cycleCountTask.expectedQuantity}
                </div>
              </FormCard>

              <div style={{ textAlign: "center", margin: "24px 0 16px", color: "#666" }}>
                Enter counted quantity:
              </div>

              <QuantityControls>
                <QuantityButton onClick={() => setCountedQuantity(Math.max(0, countedQuantity - 1))}>
                  -
                </QuantityButton>
                <QuantityDisplay
                  type="text"
                  inputMode="numeric"
                  aria-label="Counted quantity"
                  value={countedQuantity}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setCountedQuantity(0);
                    } else if (/^\d+$/.test(raw)) {
                      setCountedQuantity(Number(raw));
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                />
                <QuantityButton onClick={() => setCountedQuantity(countedQuantity + 1)}>
                  +
                </QuantityButton>
              </QuantityControls>

              {hasQuantityDiscrepancy && (
                <QuantityWarning>
                  <QuantityWarningIcon>!</QuantityWarningIcon>
                  <span>Count Discrepancy Detected</span>
                </QuantityWarning>
              )}
            </Column>

            <Column>
              <ColumnTitle>SUBMISSION</ColumnTitle>

              <div style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginBottom: "auto", padding: "0 8px" }}>
                Scan <strong>Bin</strong> and <strong>SKU IDs</strong> to verify and submit the cycle count.
              </div>

              {submitError?.kind === "validation" && (
                <ValidationErrorCard>
                  <ValidationErrorTitle>{SCANNED_DATA_ERROR.title}</ValidationErrorTitle>
                  <ValidationErrorBody>
                    <ValidationErrorHeadline>
                      {SCANNED_DATA_ERROR.headline}
                    </ValidationErrorHeadline>
                    <ValidationErrorMessage>
                      {SCANNED_DATA_ERROR.description}
                    </ValidationErrorMessage>
                  </ValidationErrorBody>
                </ValidationErrorCard>
              )}

              {submitError?.kind === "server" && submitError.message && (
                <ErrorText>{submitError.message}</ErrorText>
              )}

              <VerifyButton
                disabled={submitting}
                loading={submitting}
                onClick={handleVerifyAndSubmit}
              >
                Verify & Submit
              </VerifyButton>
            </Column>
          </ModalContainer>
        ) : null}
      </Modal>

      {contextHolder}
    </PageContainer>
  );
}


const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;


const PageContainer = styled.div`
  padding: 16px 24px;
  background: #e8e8e8;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-weight: 700;
  font-size: 18px;
  color: #1f5454;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const TaskCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  color: #444;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  strong {
    color: #1f5454;
  }
`;

const GridContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 12px;
`;

const SlotsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SlotsRow = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 1}, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  min-height: 280px;
`;

const ExitButtonRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
`;

const ExitCycleCountButton = styled.button`
  padding: 28px 64px;
  border: none;
  border-radius: 10px;
  background: ${(props) => (props.disabled ? "#f0a1a5" : "#f5222d")};
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  box-shadow: ${(props) =>
    props.disabled ? "none" : "0 4px 12px rgba(245, 34, 45, 0.3)"};
  transition: background 0.2s, box-shadow 0.2s;

  &:hover:not(:disabled) {
    background: #ff4d4f;
  }

  &:active:not(:disabled) {
    background: #cf1322;
  }
`;


// ── Static Pickwall section ──

const StaticSection = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StaticSectionTitle = styled.h3`
  font-weight: 700;
  font-size: 15px;
  color: #1f5454;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const StaticSlotsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

type StaticSlotState = "active" | "complete" | "disabled";

const StaticSlotCard = styled.div<{ $state: StaticSlotState }>`
  display: flex;
  flex-direction: column;
  width: 160px;
  height: 200px;
  border-radius: 8px;
  border-width: 2px;
  border-style: ${(props) => (props.$state === "disabled" ? "dashed" : "solid")};
  border-color: ${(props) =>
    props.$state === "complete"
      ? "#52c41a"
      : props.$state === "active"
      ? "#4d9a9a"
      : "#d9d9d9"};
  background-color: ${(props) =>
    props.$state === "disabled" ? "#f5f5f5" : "#e8e8e8"};
  cursor: ${(props) => (props.$state === "active" ? "pointer" : "default")};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${(props) =>
      props.$state === "active" ? "0 2px 8px rgba(77, 154, 154, 0.3)" : "none"};
  }
`;

const StaticSlotBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #1f5454;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  padding: 8px;
`;

const StaticSlotSub = styled.span`
  color: #666;
  font-size: 12px;
  font-weight: 500;
`;

const StaticSlotCheck = styled.div`
  flex: 1;
  margin: 4px;
  border-radius: 4px;
  background-color: #52c41a;
  color: #fff;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StaticSlotFooter = styled.div`
  padding: 6px 8px;
  background-color: #d9d9d9;
  border-top: 1px solid #c6c6c6;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  color: #333;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
`;


// ── Static cycle-count modal (case breakdown) ──

const CaseListBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const CaseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
`;

const CaseRowLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CaseRowCase = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f5454;
`;

const CaseRowSku = styled.span`
  font-size: 12px;
  color: #999;
`;

const CaseRowQty = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #333;
`;

const TotalRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #1f5454;
  border-radius: 8px;
  padding: 14px 18px;
  color: #fff;
`;

const TotalRowLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TotalRowValue = styled.div`
  font-size: 26px;
  font-weight: 800;
`;


const LargeSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #d9d9d9;
  border-top: 4px solid #4d9a9a;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 50vh;
  color: #666;
  font-size: 15px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 50vh;
  color: #888;
  font-size: 15px;
`;


const ModalContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  align-items: stretch;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f7f7f7;
  border-radius: 12px;
  padding: 32px 28px;
  border: 1px solid #e8e8e8;
`;

const ColumnTitle = styled.h3`
  font-weight: 700;
  font-size: 16px;
  color: #1f5454;
  margin: 0 0 20px 0;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FormCard = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
`;

const FormLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #4d9a9a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const FormValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #333;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const QuantityWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  padding: 10px 14px;
  border: 1px solid #ffd591;
  border-radius: 8px;
  background: #fff7e6;
  color: #d46b08;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const QuantityWarningIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #d46b08;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuantityButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: #1f5454;
  color: white;
  font-size: 24px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #2a6f6f;
  }

  &:active {
    background: #164242;
  }
`;

const QuantityDisplay = styled.input`
  font-size: 48px;
  font-weight: 800;
  color: #333;
  width: 120px;
  text-align: center;
  border: none;
  border-bottom: 2px solid #ccc;
  background: transparent;
  outline: none;
  padding: 0;

  &:focus {
    border-bottom-color: #4d9a9a;
  }
`;

const VerifyButton = styled(Button)`
  && {
    width: 100%;
    height: 52px;
    border-radius: 26px;
    font-weight: 700;
    font-size: 16px;
    background: ${(props: any) => (props.disabled ? "#cccccc" : "#07a568")};
    border: none;
    color: #fff;
    box-shadow: ${(props: any) =>
      props.disabled ? "none" : "0 2px 6px rgba(7, 165, 104, 0.3)"};
    margin-top: auto;

    &:hover:not(:disabled) {
      background: #08c77e !important;
      color: #fff !important;
    }

    &:active:not(:disabled) {
      background: #069055 !important;
    }

    &:disabled {
      color: #fff !important;
      cursor: not-allowed;
    }
  }
`;

const ErrorText = styled.div`
  width: 90%;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #cf1322;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
`;

const ValidationErrorCard = styled.div`
  width: 100%;
  margin-bottom: 16px;
  text-align: center;
`;

const ValidationErrorTitle = styled.div`
  margin-bottom: 18px;
  color: #f5222d;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const ValidationErrorBody = styled.div`
  padding: 20px 18px;
  border: 1px solid #ffccc7;
  border-radius: 14px;
  background: #fff1f0;
  color: #cf1322;
`;

const ValidationErrorHeadline = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
`;

const ValidationErrorMessage = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
`;
